<template>
    <AdminLayout>
        <template #header>
            <Header :menu="main_menu" v-if="$page.props.auth.user" />
        </template>
        <template #default>
            <div class="space-y-6">
                <!-- Header -->
                <div class="flex items-center justify-between">
                    <h1 class="text-2xl font-bold text-gray-900">Undangan Rapat</h1>
                    <Link :href="route('undangan.create')"
                        class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase transition duration-150 ease-in-out bg-red-600 border border-transparent rounded-md hover:bg-red-700">
                    <i class="mr-2 fas fa-plus"></i>
                    Tambah Undangan
                    </Link>
                </div>

                <!-- Filter Section -->
                <div class="bg-white shadow-lg rounded-lg p-6">
                    <form @submit.prevent="applyFilters" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label for="filter_judul_rapat" class="block text-sm font-medium text-gray-700 mb-1">
                                Judul Rapat
                            </label>
                            <input type="text" id="filter_judul_rapat" v-model="filters.judul_rapat"
                                placeholder="Cari judul rapat..."
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm">
                        </div>

                        <div>
                            <label for="filter_status" class="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select id="filter_status" v-model="filters.status"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm">
                                <option value="">Semua Status</option>
                                <option value="draft">Draft</option>
                                <option value="terkirim">Terkirim</option>
                                <option value="berlangsung">Berlangsung</option>
                                <option value="selesai">Selesai</option>
                                <option value="dibatalkan">Dibatalkan</option>
                            </select>
                        </div>

                        <div>
                            <label for="filter_tanggal" class="block text-sm font-medium text-gray-700 mb-1">
                                Tanggal Rapat
                            </label>
                            <input type="date" id="filter_tanggal" v-model="filters.tanggal"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm">
                        </div>

                        <div class="flex items-end space-x-2">
                            <button type="submit"
                                class="flex-1 inline-flex justify-center items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 transition ease-in-out duration-150">
                                <i class="fas fa-search mr-2"></i>
                                Filter
                            </button>
                            <button type="button" @click="resetFilters"
                                class="inline-flex justify-center items-center px-4 py-2 bg-gray-300 border border-transparent rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-400 transition ease-in-out duration-150">
                                <i class="fas fa-undo mr-2"></i>
                                Reset
                            </button>
                        </div>
                    </form>
                </div>

                <!-- Alert Messages -->
                <div v-if="$page.props.flash.success" class="bg-green-50 border border-green-200 rounded-md p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <i class="fas fa-check-circle text-green-400"></i>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm font-medium text-green-800">
                                {{ $page.props.flash.success }}
                            </p>
                        </div>
                    </div>
                </div>

                <div v-if="$page.props.flash.error" class="bg-red-50 border border-red-200 rounded-md p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <i class="fas fa-exclamation-circle text-red-400"></i>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm font-medium text-red-800">
                                {{ $page.props.flash.error }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Data Cards -->
                <div v-if="undangan.data.length === 0" class="text-center py-12">
                    <i class="fas fa-envelope text-gray-400 text-5xl mb-4"></i>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">Tidak ada undangan</h3>
                    <p class="text-gray-500 mb-6">Belum ada undangan rapat yang tersedia dengan filter yang dipilih.</p>
                    <Link :href="route('undangan.create')"
                        class="inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700">
                    <i class="fas fa-plus mr-2"></i>
                    Tambah Undangan Pertama
                    </Link>
                </div>

                <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div v-for="item in undangan.data" :key="item.id"
                        class="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-red-500">
                        <div class="p-6">
                            <div class="flex items-start justify-between mb-4">
                                <div class="flex-1">
                                    <div class="flex items-center mb-2">
                                        <span :class="getStatusBadgeClass(item.status)"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3">
                                            <i :class="getStatusIcon(item.status)" class="mr-1"></i>
                                            {{ getStatusLabel(item.status) }}
                                        </span>
                                        <span class="text-xs text-gray-500">
                                            {{ formatDate(item.tanggal_rapat) }}
                                        </span>
                                    </div>
                                    <h3 class="text-lg font-medium text-gray-900 mb-2">
                                        {{ item.judul_rapat }}
                                    </h3>
                                    <p class="text-sm text-gray-600 mb-3">
                                        {{ truncateText(item.agenda_rapat, 120) }}
                                    </p>
                                    <div class="space-y-1 text-sm text-gray-500">
                                        <div class="flex items-center">
                                            <i class="fas fa-clock w-4 text-center mr-2"></i>
                                            {{ item.waktu_mulai }} - {{ item.waktu_selesai || 'TBD' }}
                                        </div>
                                        <div class="flex items-center">
                                            <i class="fas fa-map-marker-alt w-4 text-center mr-2"></i>
                                            {{ item.tempat }}
                                        </div>
                                        <div class="flex items-center">
                                            <i class="fas fa-user w-4 text-center mr-2"></i>
                                            {{ item.penyelenggara }}
                                        </div>
                                        <div class="flex items-center">
                                            <i class="fas fa-users w-4 text-center mr-2"></i>
                                            {{ Array.isArray(item.peserta) ? item.peserta.length : 0 }} peserta
                                        </div>
                                        <div v-if="item.file_lampiran" class="flex items-center">
                                            <i class="fas fa-paperclip w-4 text-center mr-2"></i>
                                            Ada lampiran
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center justify-between pt-4 border-t border-gray-200">
                                <div class="text-xs text-gray-500">
                                    Dibuat: {{ formatDateTime(item.created_at) }}
                                </div>
                                <div class="flex items-center space-x-2">
                                    <button @click="viewUndangan(item)" class="text-gray-600 hover:text-gray-900"
                                        title="Lihat">
                                        <i class="fas fa-eye"></i>
                                    </button>
                                    <button v-if="item.status === 'draft'" @click="sendUndangan(item)"
                                        class="text-green-600 hover:text-green-900" title="Kirim">
                                        <i class="fas fa-paper-plane"></i>
                                    </button>
                                    <Link :href="route('undangan.edit', item.id)"
                                        class="text-blue-600 hover:text-blue-900" title="Edit">
                                    <i class="fas fa-edit"></i>
                                    </Link>
                                    <button @click="confirmDelete(item)" class="text-red-600 hover:text-red-900"
                                        title="Hapus">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="undangan.data.length > 0" class="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div class="px-4 py-3 border-t border-gray-200 sm:px-6">
                        <div class="flex items-center justify-between">
                            <div class="flex-1 flex justify-between sm:hidden">
                                <Link v-if="undangan.prev_page_url" :href="undangan.prev_page_url"
                                    class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                                Previous
                                </Link>
                                <Link v-if="undangan.next_page_url" :href="undangan.next_page_url"
                                    class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                                Next
                                </Link>
                            </div>
                            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p class="text-sm text-gray-700">
                                        Menampilkan
                                        <span class="font-medium">{{ undangan.from || 0 }}</span>
                                        sampai
                                        <span class="font-medium">{{ undangan.to || 0 }}</span>
                                        dari
                                        <span class="font-medium">{{ undangan.total }}</span>
                                        hasil
                                    </p>
                                </div>
                                <div>
                                    <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                        aria-label="Pagination">
                                        <!-- Previous Page Link -->
                                        <Link v-if="undangan.prev_page_url" :href="undangan.prev_page_url"
                                            class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                                        <span class="sr-only">Previous</span>
                                        <i class="fas fa-chevron-left"></i>
                                        </Link>

                                        <!-- Page Numbers -->
                                        <template v-for="link in undangan.links.slice(1, -1)" :key="link.label">
                                            <Link v-if="link.url" :href="link.url" :class="[
                                                link.active
                                                    ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                                                'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                                            ]">
                                            {{ link.label }}
                                            </Link>
                                            <span v-else
                                                class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                                {{ link.label }}
                                            </span>
                                        </template>

                                        <!-- Next Page Link -->
                                        <Link v-if="undangan.next_page_url" :href="undangan.next_page_url"
                                            class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                                        <span class="sr-only">Next</span>
                                        <i class="fas fa-chevron-right"></i>
                                        </Link>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
                role="dialog" aria-modal="true">
                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeDeleteModal">
                    </div>
                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                    <div
                        class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <i class="fas fa-exclamation-triangle text-red-600"></i>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">Konfirmasi
                                        Hapus</h3>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            Apakah Anda yakin ingin menghapus undangan rapat
                                            <strong>{{ itemToDelete?.judul_rapat }}</strong>?
                                            Tindakan ini tidak dapat dibatalkan.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button @click="deleteItem" type="button"
                                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm">
                                <i class="fas fa-trash mr-2"></i>
                                Hapus
                            </button>
                            <button @click="closeDeleteModal" type="button"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                Batal
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer></template>
    </AdminLayout>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import AdminLayout from '@/Layouts/Container.vue'
import Header from "@/Components/Header.vue";

const props = defineProps({
    undangan: Object,
    main_menu: {
        type: Array,
        required: true,
    },
})

const filters = reactive({
    judul_rapat: '',
    status: '',
    tanggal: ''
})

const showDeleteModal = ref(false)
const itemToDelete = ref(null)

const applyFilters = () => {
    router.get(route('undangan.index'), filters, {
        preserveState: true,
        preserveScroll: true
    })
}

const resetFilters = () => {
    Object.keys(filters).forEach(key => {
        filters[key] = ''
    })
    router.get(route('undangan.index'), {}, {
        preserveState: true,
        preserveScroll: true
    })
}

const viewUndangan = (item) => {
    router.visit(route('undangan.show', item.id))
}

const sendUndangan = (item) => {
    router.post(route('undangan.send', item.id), {}, {
        onSuccess: () => {
            // Success message will be handled by flash message
        }
    })
}

const confirmDelete = (item) => {
    itemToDelete.value = item
    showDeleteModal.value = true
}

const closeDeleteModal = () => {
    showDeleteModal.value = false
    itemToDelete.value = null
}

const deleteItem = () => {
    if (itemToDelete.value) {
        router.delete(route('undangan.destroy', itemToDelete.value.id), {
            onSuccess: () => {
                closeDeleteModal()
            }
        })
    }
}

const getStatusBadgeClass = (status) => {
    const classes = {
        'draft': 'bg-gray-100 text-gray-800',
        'terkirim': 'bg-blue-100 text-blue-800',
        'berlangsung': 'bg-yellow-100 text-yellow-800',
        'selesai': 'bg-green-100 text-green-800',
        'dibatalkan': 'bg-red-100 text-red-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusIcon = (status) => {
    const icons = {
        'draft': 'fas fa-file-alt',
        'terkirim': 'fas fa-paper-plane',
        'berlangsung': 'fas fa-play-circle',
        'selesai': 'fas fa-check-circle',
        'dibatalkan': 'fas fa-times-circle'
    }
    return icons[status] || 'fas fa-circle'
}

const getStatusLabel = (status) => {
    const labels = {
        'draft': 'Draft',
        'terkirim': 'Terkirim',
        'berlangsung': 'Berlangsung',
        'selesai': 'Selesai',
        'dibatalkan': 'Dibatalkan'
    }
    return labels[status] || status
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-'
    const date = new Date(dateTimeString)
    return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const truncateText = (text, limit) => {
    if (!text) return ''
    return text.length > limit ? text.substring(0, limit) + '...' : text
}
</script>